import Focusable from '../focus/Focusable';
import InputController from '../InputController';
import * as KeyHelper from './KeyHelper';
import TooltipManager from '../ui/tooltip/TooltipManager';
import KeyCode from '../input/KeyCode';
import KeyManager from '../input/KeyManager';
var documentLeftDown = false;
var documentRightDown = false;
var documentMouseDownEvent = null;
function isHTMLElementWithPixelRatio(element) {
    return !!element.pixelRatio;
}
// We have to tack the button status ourselves because move events don't reliably tell us
// if a button is down.
var staticMouseDown = function (event) {
    documentMouseDownEvent = event;
    if (event.button === 0) {
        documentLeftDown = true;
    }
    else if (event.button === 2) {
        documentRightDown = true;
    }
};
var staticMouseUp = function (event) {
    if (event.button === 0) {
        documentLeftDown = false;
    }
    else if (event.button === 2) {
        documentRightDown = false;
    }
};
// IE11 fires a mousedown, but no mouseup when clicking on the scrollbar.
//   So we listen to mousemove and check the buttons state.
//
// https://connect.microsoft.com/IE/feedback/details/783058/scrollbar-trigger-mousedown-but-not-mouseup
//
// The bug was marked as won't fix.
//
// From Microsoft:
// > Thank you for your feedback. We were able to validate your feedback. However, this bug may
// > not be a typical developer scenario particularly as more browsing happens on devices that are
// > touch centric, we will not be able to address this bug during this release.
//
var staticMouseMove = function (event) {
    // tslint:disable-next-line:strict-type-predicates
    if (event.buttons !== undefined) {
        //Safari does not support buttons
        // bitwise intentional
        if ((event.buttons & 1) !== 1) {
            // eslint-disable-line no-bitwise
            documentLeftDown = false;
        }
        else if ((event.buttons & 2) !== 2) {
            // eslint-disable-line no-bitwise
            documentRightDown = false;
        }
    }
};
if (!sas.ltjs.node) {
    //listen on body so that jQuery mobile won't prevent us from getting right mouse clicks.
    if (window.document.body) {
        window.document.body.addEventListener('mousedown', staticMouseDown);
        window.document.body.addEventListener('mouseup', staticMouseUp);
        //In order to listen for mouse up events outside the browser window, must add event listener to document as well
        window.document.addEventListener('mouseup', staticMouseUp);
        window.document.body.addEventListener('mousemove', staticMouseMove);
    }
    else {
        var attachStaticEventListeners_1 = function () {
            window.removeEventListener('load', attachStaticEventListeners_1);
            window.document.body.addEventListener('mousedown', staticMouseDown);
            window.document.body.addEventListener('mouseup', staticMouseUp);
            window.document.addEventListener('mouseup', staticMouseUp);
            window.document.body.addEventListener('mousemove', staticMouseMove);
        };
        window.addEventListener('load', attachStaticEventListeners_1);
    }
}
var GestureHelper = /** @class */ (function () {
    function GestureHelper(baseView, domElement, overrides, displayZoomOverlay) {
        this._baseView = null;
        this._leftButtonDown = false;
        this._rightButtonDown = false;
        this._windowListeners = [];
        this._elementListeners = [];
        this._lastMouseMovePosition = null;
        this._lastWindowMouseMovePosition = null;
        // Added check to track component being destroyed to handle a possible case of state change when _callOverrides is called.
        this._isDestroyed = false;
        this.overrides = overrides || {};
        if (baseView) {
            this._baseView = baseView.retain();
        }
        this._element = domElement;
        this._inputController = new InputController().retain();
        this._inputController.setView(baseView);
        this._addInputListeners();
        this._isMac = navigator.appVersion.indexOf('Mac') !== -1;
        this._displayZoomOverlay = displayZoomOverlay;
    }
    GestureHelper.prototype._addInputListeners = function () {
        // NOTE(mikcol): added throw
        if (!this._windowListeners) {
            throw new Error();
        }
        // these listeners are actually added on mouseenter
        // tslint:disable:ban
        // tslint:disable:no-unsafe-any
        this._windowListeners[0] = this._onWindowMouseUp.bind(this);
        this._windowListeners[1] = this._onWindowMouseMove.bind(this);
        // tslint:enable:ban
        // tslint:enable:no-unsafe-any
        // NOTE(mikcol): added throw
        if (!this._elementListeners) {
            throw new Error();
        }
        // tslint:disable:ban
        // tslint:disable:no-unsafe-any
        this._elementListeners[0] = this._onMouseDown.bind(this);
        this._elementListeners[1] = this._onMouseWheel.bind(this);
        this._elementListeners[2] = this._onDoubleClick.bind(this);
        this._elementListeners[3] = this._onMouseEnter.bind(this);
        this._elementListeners[4] = this._onMouseLeave.bind(this);
        this._elementListeners[5] = this._onMouseMove.bind(this);
        this._elementListeners[6] = this._onFocus.bind(this);
        this._elementListeners[7] = this._onBlur.bind(this);
        this._elementListeners[8] = this._onMouseUp.bind(this);
        // tslint:enable:ban
        // tslint:enable:no-unsafe-any
        // NOTE(mikcol): added throw
        if (!this._element) {
            throw new Error();
        }
        this._element.addEventListener('mousedown', this._elementListeners[0]);
        this._element.addEventListener('wheel', this._elementListeners[1]);
        this._element.addEventListener('dblclick', this._elementListeners[2]);
        this._element.addEventListener('mouseenter', this._elementListeners[3]);
        this._element.addEventListener('mouseleave', this._elementListeners[4]);
        this._element.addEventListener('mousemove', this._elementListeners[5]);
        this._element.addEventListener('focus', this._elementListeners[6]);
        this._element.addEventListener('blur', this._elementListeners[7]);
        this._element.addEventListener('mouseup', this._elementListeners[8]);
    };
    GestureHelper.prototype._callOverrides = function (eventOverrides, args) {
        if (eventOverrides) {
            // Traverse the override functions from the end, calling
            // the function and checking the return value.
            // Return value of true prevents all other overrides from
            // executing and returns true.
            for (var i = eventOverrides.length - 1; i >= 0; i--) {
                var callback = eventOverrides[i];
                if (callback && callback(args)) {
                    return true;
                }
            }
        }
        return false;
    };
    GestureHelper.prototype._onMouseDown = function (event) {
        if (this._isDestroyed) {
            return;
        }
        KeyHelper.verifyModifierKeyState(event);
        var mousePosition = GestureHelper.getMousePosition(event, this._element);
        if (event.button === 0) {
            this._leftButtonDown = true;
        }
        else if (event.button === 2) {
            this._rightButtonDown = true;
        }
        this._inputController.handleMouseDown(mousePosition.x, mousePosition.y, this._element.offsetWidth, this._element.offsetHeight, this._leftButtonDown, this._rightButtonDown);
        // NOTE(mikcol): doesn't seem like this value is used
        // this._mouseDownPosition = mousePosition;
    };
    GestureHelper.prototype._onMouseUp = function () {
        this.addWindowListeners();
    };
    GestureHelper.prototype._onWindowMouseUp = function (event) {
        if (this._isDestroyed) {
            return;
        }
        KeyHelper.verifyModifierKeyState(event);
        var mousePosition = GestureHelper.getMousePosition(event, this._element, true);
        //With mouse up event listener on both body and document, this code path almost always executes twice.
        //Don't add code before this that doesn't need to be executed. The second pass through will return.
        if (event.button === 0 && this._leftButtonDown) {
            this._leftButtonDown = false;
        }
        else if (event.button === 2 && this._rightButtonDown) {
            this._rightButtonDown = false;
        }
        else {
            return;
        }
        if (!documentMouseDownEvent) {
            return;
        }
        if (!mousePosition.isMine) {
            this.removeWindowListeners();
        }
        //When applying jQuery UI's draggable functionality to portable components, it causes weird behavior in
        // regards to focus management. This fix forces a call to jQuery's focus function in order to return focus
        // handling to normal functionality.
        if (this._element.classList.contains('ui-draggable') && jQuery) {
            jQuery('#' + this._element.id).focus();
        }
        var documentMouseDownPosition = GestureHelper.getMousePosition(documentMouseDownEvent, this._element, true);
        var leftButton = event.button === 0;
        // NOTE(mikcol): added explicit number conversion for undefined => NaN
        var xDelta = Math.abs(Number(mousePosition.x) - Number(documentMouseDownPosition.x));
        var yDelta = Math.abs(Number(mousePosition.y) - Number(documentMouseDownPosition.y));
        if (!leftButton ||
            xDelta > 3 ||
            yDelta > 3 ||
            (this.overrides &&
                !this._callOverrides(this.overrides.mouseUp, {
                    localX: mousePosition.localX,
                    x: mousePosition.x,
                    localY: mousePosition.localY,
                    y: mousePosition.y,
                    ctrlKey: this._isMac ? event.metaKey : event.ctrlKey,
                    sourceEvent: event,
                }))) {
            this._inputController.handleMouseUp(mousePosition.x, mousePosition.y, this._element.offsetWidth, this._element.offsetHeight, leftButton, event.button === 2);
        }
        // NOTE(mikcol): doesn't seem like this value is used
        // this._mouseDownPosition = null;
        if (leftButton && document.releaseCapture) {
            // tslint:disable-next-line:no-unsafe-any
            document.releaseCapture();
        }
    };
    GestureHelper.prototype._onWindowMouseMove = function (event) {
        if (this._isDestroyed) {
            return;
        }
        KeyHelper.verifyModifierKeyState(event);
        var mousePosition = GestureHelper.getMousePosition(event, this._element, true);
        var lastPosition = this._lastWindowMouseMovePosition;
        if (lastPosition && lastPosition.x === mousePosition.x && lastPosition.y === mousePosition.y) {
            // IE constantly sends mouse events even when the mouse isn't moving. BLOCK THIS!
            return;
        }
        this._lastWindowMouseMovePosition = mousePosition;
        if (event.preventDefault) {
            if ((documentLeftDown && this._leftButtonDown) || (documentRightDown && this._rightButtonDown)) {
                event.preventDefault();
            }
        }
        if (this._leftButtonDown || mousePosition.isMine) {
            this._inputController.handleMouseMove(mousePosition.x, mousePosition.y, this._element.offsetWidth, this._element.offsetHeight, this._leftButtonDown, this._rightButtonDown);
        }
    };
    GestureHelper.prototype._onMouseMove = function (event) {
        if (this._isDestroyed) {
            return;
        }
        var mousePosition = GestureHelper.getMousePosition(event, this._element, true);
        var lastPosition = this._lastMouseMovePosition;
        if (lastPosition && lastPosition.x === mousePosition.x && lastPosition.y === mousePosition.y) {
            // IE constantly sends mouse events even when the mouse isn't moving. BLOCK THIS!
            return;
        }
        // hide any active tips during pan
        if (this._leftButtonDown) {
            this._hideTooltips();
        }
        this._lastMouseMovePosition = mousePosition;
        // NOTE(mikcol): was (documentRightDown && !this.rightButton)
        var cantTouchThis = (documentLeftDown && !this._leftButtonDown) || (documentRightDown && !this._rightButtonDown);
        if (cantTouchThis) {
            return;
        }
        KeyHelper.verifyModifierKeyState(event);
        mousePosition = GestureHelper.getMousePosition(event, this._element);
        if (!this._leftButtonDown && !this._rightButtonDown && this.overrides) {
            this._callOverrides(this.overrides.hover, {
                localX: mousePosition.localX,
                x: mousePosition.x,
                localY: mousePosition.localY,
                y: mousePosition.y,
                sourceEvent: event,
            });
        }
        // For IE setCapture cannot be done on mouseDown without interfering with blur and focus
        // events. The purpose of this is that once a pan gesture has started, other things on
        // the page should not get mouse events. Things like text selection outside of the graph
        // can happen otherwise.
        if (this._leftButtonDown && this._element.setCapture) {
            // tslint:disable-next-line:no-unsafe-any
            this._element.setCapture();
        }
    };
    GestureHelper.prototype._onMouseWheel = function (event) {
        var _a;
        if (this._isDestroyed) {
            return;
        }
        if (!KeyManager.isPressed(KeyCode.PVK_CONTROL) && ((_a = this._displayZoomOverlay) === null || _a === void 0 ? void 0 : _a.call(this))) {
            return;
        }
        this._hideTooltips();
        KeyHelper.verifyModifierKeyState(event);
        var mousePosition = GestureHelper.getMousePosition(event, this._element);
        var delta;
        // tslint:disable-next-line:strict-type-predicates
        if (event.wheelDelta !== undefined && event.wheelDelta !== null) {
            delta = event.wheelDelta;
        }
        else {
            delta = -event.deltaY;
        }
        var adjustedDelta = delta < 0 ? -120 : 120; //adjusting based on InputController's assumptions
        var handled = this._inputController.handleMouseWheel(mousePosition.x, mousePosition.y, adjustedDelta, this._element.offsetWidth, this._element.offsetHeight);
        if (this.overrides) {
            this._callOverrides(this.overrides.mouseWheel, {
                localX: mousePosition.localX,
                x: mousePosition.x,
                localY: mousePosition.localY,
                y: mousePosition.y,
                delta: delta,
                sourceEvent: event,
            });
        }
        if (handled && event.preventDefault) {
            event.preventDefault();
        }
    };
    GestureHelper.prototype._onDoubleClick = function (event) {
        if (this._isDestroyed) {
            return;
        }
        this._hideTooltips();
        KeyHelper.verifyModifierKeyState(event);
        var mousePosition = GestureHelper.getMousePosition(event, this._element);
        var argObj = {
            localX: mousePosition.localX,
            x: mousePosition.x,
            localY: mousePosition.localY,
            y: mousePosition.y,
            sourceEvent: event,
        };
        if (this.overrides && !this._callOverrides(this.overrides.doubleClick, argObj)) {
            this._inputController.handleMouseDoubleClick(mousePosition.x, mousePosition.y, event.button === 0);
            this._callOverrides(this.overrides.afterDoubleClick, argObj);
        }
    };
    GestureHelper.prototype._onMouseEnter = function (event) {
        if (this._isDestroyed) {
            return;
        }
        KeyHelper.verifyModifierKeyState(event);
        this._inputController.handleMouseEnter();
        if (this.overrides) {
            this._callOverrides(this.overrides.mouseEnter, { sourceEvent: event });
        }
        this.addWindowListeners();
    };
    GestureHelper.prototype._onMouseLeave = function (event) {
        if (this._isDestroyed) {
            return;
        }
        this._inputController.handleMouseLeave();
        if (this.overrides) {
            this._callOverrides(this.overrides.mouseLeave, { sourceEvent: event });
        }
        Focusable.resignMouseFocus();
        if (!this._leftButtonDown && !this._rightButtonDown) {
            this.removeWindowListeners();
        }
    };
    GestureHelper.prototype._onFocus = function ( /*event: FocusEvent*/) {
        this._inputController.handleFocus();
    };
    GestureHelper.prototype._onBlur = function ( /*event: FocusEvent*/) {
        this._inputController.handleBlur();
    };
    GestureHelper.prototype._hideTooltips = function () {
        if (!this._element) {
            return;
        }
        TooltipManager.instance().hideTooltips(this._element.id);
    };
    GestureHelper.prototype.destroy = function () {
        this._isDestroyed = true;
        if (this._baseView) {
            this._baseView.release();
            this._baseView = null;
        }
        if (this._inputController) {
            this._inputController.release();
            this._inputController = null;
        }
        this.removeWindowListeners();
        if (this._elementListeners && this._element) {
            this._element.removeEventListener('mousedown', this._elementListeners[0]);
            this._element.removeEventListener('wheel', this._elementListeners[1]);
            this._element.removeEventListener('dblclick', this._elementListeners[2]);
            this._element.removeEventListener('mouseenter', this._elementListeners[3]);
            this._element.removeEventListener('mouseleave', this._elementListeners[4]);
            this._element.removeEventListener('mousemove', this._elementListeners[5]);
            this._element.removeEventListener('focus', this._elementListeners[6]);
            this._element.removeEventListener('blur', this._elementListeners[7]);
            this._element.removeEventListener('mouseup', this._elementListeners[8]);
        }
        this._element = null;
        this._windowListeners = null;
        this._elementListeners = null;
        this.overrides = null;
    };
    GestureHelper.prototype.removeWindowListeners = function () {
        if (this._windowListeners) {
            window.document.body.removeEventListener('mouseup', this._windowListeners[0]);
            window.document.removeEventListener('mouseup', this._windowListeners[0]);
            window.document.body.removeEventListener('mousemove', this._windowListeners[1]);
        }
    };
    GestureHelper.prototype.addWindowListeners = function () {
        if (!this._windowListeners) {
            throw new Error();
        }
        // calling add multiple times shouldn't be a problem
        // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Multiple_identical_event_listeners
        window.document.body.addEventListener('mouseup', this._windowListeners[0]);
        window.document.addEventListener('mouseup', this._windowListeners[0]);
        window.document.body.addEventListener('mousemove', this._windowListeners[1]);
    };
    GestureHelper.getPositionFromPoint = function (point, element) {
        // NOTE(mikcol): isMine default value is now false
        var y = 0, isMine = false;
        var x = point.x, localX = point.x, localY = point.y;
        if (isHTMLElementWithPixelRatio(element)) {
            y = element.clientHeight / element.pixelRatio - point.y;
        }
        else {
            y = element.clientHeight - point.y;
        }
        if (x >= 0 && x < element.offsetWidth && y >= 0 && y < element.offsetHeight) {
            isMine = true;
        }
        return {
            x: x,
            y: y,
            localX: localX,
            localY: localY,
            isMine: isMine,
        };
    };
    GestureHelper.getMousePosition = function (event, element, isDocumentEvent) {
        var x = 0, y = 0;
        if (!event || !element) {
            // NOTE(mikcol): legacy behavior, calling code doesn't seem to handle this
            // TODO(mickol): consider throwing instead
            // tslint:disable-next-line:no-object-literal-type-assertion
            return {};
        }
        // S1319485: Avoid IE bug by checking parentElement before calling getBoundingClientRect().
        if (isDocumentEvent && element.parentElement) {
            var elementbounds = element.getBoundingClientRect();
            x = event.clientX - elementbounds.left;
            y = event.clientY - elementbounds.top;
        }
        else {
            x = event.offsetX;
            y = event.offsetY;
        }
        return GestureHelper.getPositionFromPoint({ x: x, y: y }, element);
    };
    GestureHelper.removeStaticEventListeners = function () {
        // tslint:disable-next-line:no-unbound-method
        window.removeEventListener('unload', GestureHelper.removeStaticEventListeners);
        window.document.body.removeEventListener('mousedown', staticMouseDown);
        window.document.body.removeEventListener('mouseup', staticMouseUp);
        window.document.removeEventListener('mouseup', staticMouseUp);
        window.document.body.removeEventListener('mousemove', staticMouseMove);
    };
    return GestureHelper;
}());
export default GestureHelper;
if (!sas.ltjs.node) {
    // tslint:disable-next-line:no-unbound-method
    window.addEventListener('unload', GestureHelper.removeStaticEventListeners);
}
